import {
  Box,
  FormControlLabel,
  FormGroup,
  Slider,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LoggingContext } from '../../Context/LoggingContext/LoggingContext';
import { UserContext } from '../../Context/UserContext/UserContextProvider';
import { teesPropertiesObject } from '../../tools/generalProperties';
import { TeeMeasuresLabels } from '../../types/contexts/userContext';
import { FilterAction, FilterObjectSlider } from '../../types/reducers/filterReducer';

export function convertInches(cm: number): number {
  return Math.round((cm / 2.57) * 2) / 2;
}

export function convertToCm(inches: number): number {
  return Math.round(inches * 2.57);
}

function SizeSlider({
  filterLabel,
  filterObject,
  dispatchFilter,
  dropdownOpen,
}: {
  filterLabel: TeeMeasuresLabels;
  filterObject: FilterObjectSlider;
  dispatchFilter: React.Dispatch<FilterAction>;
  dropdownOpen: boolean;
}) {
  const location = useLocation();
  const { userDetailsState } = useContext(UserContext);
  const idealValue = userDetailsState.idealGarmentMeasurements[filterLabel] || 0;
  const unitsConverted = {
    label: userDetailsState.unitcm === 1 ? 'cm' : '"',
    idealValue: userDetailsState.unitcm === 1 ? idealValue : convertInches(idealValue),
    sliderMin:
      userDetailsState.unitcm === 1
        ? teesPropertiesObject[filterLabel].min
        : convertInches(teesPropertiesObject[filterLabel].min),
    sliderMax:
      userDetailsState.unitcm === 1
        ? teesPropertiesObject[filterLabel].max
        : convertInches(teesPropertiesObject[filterLabel].max),
    filterObjectMin:
      userDetailsState.unitcm === 1 ? filterObject.min : convertInches(filterObject.min),
    filterObjectMax:
      userDetailsState.unitcm === 1 ? filterObject.max : convertInches(filterObject.max),
  };

  const { addLog } = useContext(LoggingContext);
  const [disabledState, setDisabledState] = React.useState(filterObject.disabled);
  const [tooltipOpenState, setTooltipOpenState] = React.useState(false);
  const [value, setValue] = React.useState<number[]>([
    unitsConverted.filterObjectMin,
    unitsConverted.filterObjectMax,
  ]);
  const unitsConvertedLive = {
    valueState0: userDetailsState.unitcm === 1 ? value[0] : convertToCm(value[0]),
    valueState1: userDetailsState.unitcm === 1 ? value[1] : convertToCm(value[1]),
  };
  // Close tooltip after a short time (tooltip opens when toggle the disable switch)
  useEffect(() => {
    // If we just opened the dropdown, then show the tooltip
    if (dropdownOpen && tooltipOpenState === false && disabledState) {
      setTooltipOpenState(true);
    } else if (!dropdownOpen && tooltipOpenState === true) {
      setTooltipOpenState(false);
    }
    if (!disabledState) {
      setTooltipOpenState(false);
    }

    const timer1 = setTimeout(() => {
      if (dropdownOpen) {
        setTooltipOpenState(false);
      }
    }, 2000);
    return () => {
      clearTimeout(timer1);
    };
  }, [dropdownOpen, disabledState]);

  // Debounce update to filterState from local state
  useEffect(() => {
    function updateWhenSliderThumbMoved() {
      // Return out if local state matches filterState
      if (
        unitsConverted.filterObjectMin === value[0] &&
        unitsConverted.filterObjectMax === value[1]
      )
        return;
      dispatchFilter({
        type: 'update slider',
        filterLabel,
        min: unitsConvertedLive.valueState0,
        max: unitsConvertedLive.valueState1,
      });
    }
    const delayDebounceFn = setTimeout(() => {
      updateWhenSliderThumbMoved();
    }, 150);
    return () => clearTimeout(delayDebounceFn);
  }, [value[0], value[1]]);

  // Update local state from parent state
  useEffect(() => {
    // Return out if local state matches filterState
    if (
      unitsConverted.filterObjectMin === value[0] &&
      unitsConverted.filterObjectMax === value[1] &&
      disabledState === filterObject.disabled
    )
      return;
    setValue([unitsConverted.filterObjectMin, unitsConverted.filterObjectMax]);
    setDisabledState(filterObject.disabled);
  }, [filterObject.min, filterObject.max, filterObject.disabled]);

  // Debounce disable/enable slider from local state
  useEffect(() => {
    function updateWhenDisabledToggled() {
      // If the disabled state is already equal to the filterState then return
      if (filterObject.disabled === disabledState) return;
      if (!disabledState) {
        // If we enable slider (disabledState=false), then update slider:
        dispatchFilter({
          type: 'update slider',
          filterLabel,
          min: unitsConvertedLive.valueState0,
          max: unitsConvertedLive.valueState1,
        });
      } else {
        dispatchFilter({
          type: 'disable slider',
          filterLabel,
        });
      }
    }
    const delayDebounceFn = setTimeout(() => {
      updateWhenDisabledToggled();
    }, 100);
    return () => clearTimeout(delayDebounceFn);
  }, [disabledState]);

  // Converts to whole number if cm, or to nearest 0.5 if inches
  function convertToReadableValue(initialValue: number): number {
    if (userDetailsState.unitcm === 1) {
      return Math.round(initialValue);
    }
    return Math.round(initialValue * 2) / 2;
  }

  return (
    <Box sx={{ width: '80%', margin: 'auto' }}>
      <br />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          id={`input-slider-${filterLabel}`}
          variant='subtitle2'
          gutterBottom
          sx={{
            textTransform: 'capitalize',
            alignSelf: 'center',
            fontWeight: 'bold',
            whiteSpace: 'pre-line',
          }}
        >
          {`${teesPropertiesObject[filterLabel].name}:
          ${convertToReadableValue(value[0])}${unitsConverted.label} - ${convertToReadableValue(
            value[1]
          )}${unitsConverted.label}`}
        </Typography>
        <FormGroup>
          <Tooltip placement='bottom' title='Click to enable slider' open={tooltipOpenState} arrow>
            <FormControlLabel
              control={
                <Switch
                  checked={!disabledState}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    addLog(`Clicked enable/disable button for ${filterLabel} slider`, location.pathname);
                    setDisabledState(!event.target.checked);
                  }}
                  color='success'
                />
              }
              labelPlacement='start'
              label={
                <Typography
                  id={`input-label-${filterLabel}`}
                  variant='caption'
                  gutterBottom
                  sx={{
                    textTransform: 'capitalize',
                  }}
                  color='success'
                >
                  {`${disabledState ? 'Disabled' : 'Enabled'}`}
                </Typography>
              }
            />
          </Tooltip>
        </FormGroup>
      </Box>
      <Slider
        getAriaLabel={() => filterLabel}
        value={value}
        onChange={(event: Event, newValue: number | number[], activeThumb: number) => {
          if (!Array.isArray(newValue)) {
            return;
          }
          // const minimumThumbSeparation = convertToReadableValue(
          //   (unitsConverted.sliderMax - unitsConverted.sliderMin) / 10
          // );
          const minimumThumbSeparation = userDetailsState.unitcm ? 2 : 1;
          if (activeThumb === 0) {
            setValue([Math.min(newValue[0], value[1] - minimumThumbSeparation), value[1]]);
          } else {
            setValue([value[0], Math.max(newValue[1], value[0] + minimumThumbSeparation)]);
          }
        }}
        valueLabelDisplay='auto'
        disableSwap
        min={unitsConverted.sliderMin}
        max={unitsConverted.sliderMax}
        step={userDetailsState.unitcm === 1 ? 1 : 0.5}
        disabled={disabledState}
        marks={[
          {
            value: unitsConverted.idealValue || 0,
            label: `${unitsConverted.idealValue}${unitsConverted.label}`,
          },
        ]}
      />
    </Box>
  );
}

export default SizeSlider;
